import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getCheckoutStatus } from '@/lib/api';
import { formatPrice } from '@/lib/utils';

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    const pollStatus = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        setStatus(res.data);
        if (res.data.payment_status === 'paid') {
          setLoading(false);
          return;
        }
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.error('Status check error:', e);
        setLoading(false);
      }
    };
    pollStatus();
  }, [sessionId, pollCount]);

  const isPaid = status?.payment_status === 'paid';

  // Fire Meta Pixel Purchase event when payment confirmed
  useEffect(() => {
    if (isPaid && window.fbq) {
      window.fbq('track', 'Purchase');
    }
  }, [isPaid]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md text-center">
        {loading ? (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-gray-400 animate-spin" />
            <h2 className="font-heading text-2xl text-gray-900 mt-6">Confirming Your Payment</h2>
            <p className="text-sm text-gray-500 font-body mt-2">Please wait while we verify your payment...</p>
          </>
        ) : isPaid ? (
          <>
            <CheckCircle className="h-10 w-10 mx-auto text-green-600" />
            <h2
              className="font-heading text-3xl text-gray-900 mt-6"
              data-testid="payment-success-message"
            >
              Your Ticket is Confirmed
            </h2>
            <p className="text-sm text-gray-500 font-body mt-3">
              Thank you for your purchase. We look forward to seeing you.
            </p>

            <Separator className="my-8 bg-gray-100" />

            <div className="text-left space-y-3 text-sm font-body">
              <div className="flex justify-between">
                <span className="text-gray-400">Event</span>
                <span className="text-gray-700 text-right">{status?.metadata?.event_title || 'Ministry Event'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Name</span>
                <span className="text-gray-700">{status?.metadata?.attendee_name || ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Email</span>
                <span className="text-gray-700">{status?.metadata?.attendee_email || ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Amount</span>
                <span className="font-heading font-medium text-gray-900">
                  {formatPrice((status?.amount_total || 0) / 100, status?.currency || 'usd')}
                </span>
              </div>
            </div>

            <Separator className="my-8 bg-gray-100" />

            <p className="text-xs text-gray-400 font-body">
              A confirmation has been sent to your email.
            </p>

            <Link to="/events" className="block mt-8">
              <Button
                className="h-11 px-8 rounded-sm bg-[#1a2e4a] text-white font-body text-sm"
                data-testid="payment-success-back-to-events"
              >
                Browse More Events
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </>
        ) : (
          <>
            <h2 className="font-heading text-2xl text-gray-900">Payment Status Pending</h2>
            <p className="text-sm text-gray-500 font-body mt-3">
              We're still processing your payment. Please check your email for confirmation.
            </p>
            <Link to="/events" className="block mt-8">
              <Button className="rounded-sm bg-[#1a2e4a] text-white font-body text-sm">Back to Events</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
